import { ApolloQueryResult } from "@apollo/client";

import { Trash } from "@neonumy/neonumy-icons";
import { Button, Modal } from "@neonumy/neonumy-ui";
import { useTranslation } from "react-i18next";
import { useDeleteUserAccess } from "./hooks/useDeleteUserAccess";

interface DeleteUserAccessProps {
  /** The ID of the user whose access needs to be deleted. */
  userId: ID;
  /**  The ID of the accounting firm to be removed from the user's access. */
  accountingFirmId: ID;
  /** A function to refetch the user's access data after deletion. */
  refetch: (
    variables?: Partial<ListAccountingFirmsAccessInput> | undefined,
  ) => Promise<ApolloQueryResult<ListAccountingFirmsAccessOutput>>;
}

/**
 * A component to delete user access to a specific accounting firm.
 *
 * @returns A React component that renders a button to open a modal for deleting user access.
 */
export default function DeleteUserAccess({
  userId,
  accountingFirmId,
  refetch,
}: DeleteUserAccessProps) {
  const { t } = useTranslation();
  const { handleOk } = useDeleteUserAccess({
    userId,
    accountingFirmId,
    refetch,
  });

  const confirm = () => {
    Modal.confirm({
      title: t("Delete user access"),
      content: t("Are you sure that you want to delete the accesses to this accounting firm?"),
      okText: t("Yes"),
      okType: "danger",
      onOk: handleOk,
    });
  };

  return <Button icon={<Trash />} type="text" color="newrai-blue" onClick={confirm} />;
}
